import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getControlTokens } from '../common/controls';

/**
 * IconSwitch - A compact, standardized switch toggle with optional icon
 *
 * Used for binary toggles like:
 * - Icons/Nodes display toggle (valuation)
 * - Labels/No Labels toggle (valuation)
 * - Compare mode toggle (dashboard)
 *
 * @param {Object} props
 * @param {boolean} props.checked - Current state of the switch
 * @param {function} props.onChange - Handler when switch is toggled
 * @param {string} props.labelOn - Label when switch is ON (default: 'ON')
 * @param {string} props.labelOff - Label when switch is OFF (default: 'OFF')
 * @param {React.ReactNode} props.icon - Optional icon to display before the switch
 * @param {number} props.width - Width of the toggle container (default: 125)
 * @param {boolean} props.showIcon - Whether to show the icon (default: true if icon provided)
 * @param {boolean} props.uppercase - Whether to transform labels to uppercase (default: true)
 */
function IconSwitch({
  checked = false,
  onChange,
  labelOn = 'ON',
  labelOff = 'OFF',
  icon = null,
  width = 125,
  showIcon = true,
  uppercase = true,
  controlContext = 'filterBar',
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const trackHeight = Math.max(tokens.height - 10, 14);
  const thumbSize = Math.max(trackHeight - 2, 12);
  const switchWidth = tokens.height + 12;
  const switchOffset = switchWidth - thumbSize - 4;
  const switchTop = (tokens.height - trackHeight) / 2;

  const handleChange = (event, value) => {
    if (onChange) {
      onChange(event, value);
    }
  };

  const rawLabel = checked ? labelOn : labelOff;
  const label = uppercase ? rawLabel.toUpperCase() : rawLabel;

  return (
    <Box
      data-control-context={controlContext}
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: tokens.height,
      }}
    >
      <Box
        sx={{
          paddingInline: theme.spacing(tokens.buttonPx),
          height: tokens.height,
          backgroundColor: tokens.surface,
          width: `${width}px`,
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${tokens.borderColor}`,
          borderRadius: `${tokens.radius}px`,
          gap: theme.spacing(tokens.iconLabelGap + 0.125),
          boxSizing: 'border-box',
        }}
      >
        {/* Icon */}
        {showIcon && icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.secondary,
              flexShrink: 0,
              '& .MuiSvgIcon-root': {
                fontSize: tokens.iconSize,
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Switch */}
        <Switch
          checked={checked}
          onChange={handleChange}
          size="small"
          data-control-kind="switch"
          sx={{
            width: switchWidth,
            height: tokens.height,
            p: 0,
            flexShrink: 0,
            '& .MuiSwitch-switchBase': {
              p: 0,
              m: `${switchTop}px 0 0 2px`,
              color: theme.palette.grey[100],
              '&.Mui-checked': {
                transform: `translateX(${switchOffset}px)`,
                color: theme.palette.common.white,
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.palette.primary.main,
                  opacity: 1,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              width: thumbSize,
              height: thumbSize,
              boxShadow: 'none',
            },
            '& .MuiSwitch-track': {
              height: trackHeight,
              mt: `${switchTop}px`,
              borderRadius: trackHeight / 2,
              backgroundColor: theme.palette.mode === 'light'
                ? 'rgba(0, 0, 0, 0.26)'
                : 'rgba(255, 255, 255, 0.3)',
              opacity: 1,
            },
          }}
        />

        {/* Label with wrapping */}
        <Typography
          variant="body2"
          sx={{
            borderLeft: `1px solid ${theme.palette.divider}`,
            pl: '4px',
            color: theme.palette.text.secondary,
            fontSize: tokens.labelFontSize,
            lineHeight: 1.1,
            flex: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

export default IconSwitch;
